"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useState } from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn } from "@/lib/utils";

// Types
type TimelineContextValue = {
  activeStep: number;
  setActiveStep: (step: number) => void;
};

// Context
const TimelineContext = createContext<TimelineContextValue | undefined>(undefined);
const useTimeline = () => {
  const context = useContext(TimelineContext);
  if (!context) {
    throw new Error("useTimeline must be used within a Timeline");
  }
  return context;
};

// Components
interface TimelineProps extends useRender.ComponentProps<"div"> {
  defaultValue?: number;
  value?: number;
  onValueChange?: (value: number) => void;
  orientation?: "horizontal" | "vertical";
}
function Timeline(t0) {
  const $ = _c(13);
  const {
    defaultValue: t1,
    value,
    onValueChange,
    orientation: t2,
    className,
    render,
    children,
    ...props
  } = t0;
  const defaultValue = t1 === undefined ? 1 : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const [activeStep, setInternalStep] = useState(defaultValue);
  let t3;
  if ($[0] !== onValueChange || $[1] !== value) {
    t3 = step => {
      if (value === undefined) {
        setInternalStep(step);
      }
      onValueChange?.(step);
    };
    $[0] = onValueChange;
    $[1] = value;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const setActiveStep = t3;
  const currentStep = value ?? activeStep;
  const defaultProps = {
    className: cn("group/timeline flex data-[orientation=horizontal]:w-full data-[orientation=horizontal]:flex-row data-[orientation=vertical]:flex-col", className),
    "data-orientation": orientation,
    "data-slot": "timeline",
    children
  };
  const t4 = TimelineContext;
  let t5;
  if ($[3] !== currentStep || $[4] !== setActiveStep) {
    t5 = {
      activeStep: currentStep,
      setActiveStep
    };
    $[3] = currentStep;
    $[4] = setActiveStep;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = "div";
  const t7 = mergeProps(defaultProps, props);
  let t8;
  if ($[6] !== render || $[7] !== t7) {
    t8 = {
      defaultTagName: t6,
      render,
      props: t7
    };
    $[6] = render;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = useRender(t8);
  let t10;
  if ($[9] !== t4.Provider || $[10] !== t5 || $[11] !== t9) {
    t10 = <t4.Provider value={t5}>{t9}</t4.Provider>;
    $[9] = t4.Provider;
    $[10] = t5;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  return t10;
}

// TimelineContent
function TimelineContent(t0) {
  const $ = _c(9);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      children,
      ...props
    } = t0;
    const defaultProps = {
      className: cn("text-muted-foreground text-sm", className),
      "data-slot": "timeline-content",
      children
    };
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(defaultProps, props);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t5 = {
      defaultTagName: t1,
      render: t2,
      props: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t4(t5);
}

// TimelineDate
type TimelineDateProps = useRender.ComponentProps<"time">;
function TimelineDate(t0) {
  const $ = _c(9);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      children,
      ...props
    } = t0;
    const defaultProps = {
      className: cn("mb-1 block font-medium text-muted-foreground text-xs group-data-[orientation=vertical]/timeline:max-sm:h-4", className),
      "data-slot": "timeline-date",
      children
    };
    t4 = useRender;
    t1 = "time";
    t2 = render;
    t3 = mergeProps(defaultProps, props);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t5 = {
      defaultTagName: t1,
      render: t2,
      props: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t4(t5);
}

// TimelineHeader
function TimelineHeader(t0) {
  const $ = _c(9);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      children,
      ...props
    } = t0;
    const defaultProps = {
      className: cn(className),
      "data-slot": "timeline-header",
      children
    };
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(defaultProps, props);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t5 = {
      defaultTagName: t1,
      render: t2,
      props: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t4(t5);
}

// TimelineIndicator
type TimelineIndicatorProps = useRender.ComponentProps<"div">;
function TimelineIndicator(t0) {
  const $ = _c(9);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      children,
      render,
      ...props
    } = t0;
    const defaultProps = {
      "aria-hidden": true,
      className: cn("group-data-[orientation=horizontal]/timeline:-top-6 group-data-[orientation=horizontal]/timeline:-translate-y-1/2 group-data-[orientation=vertical]/timeline:-left-6 group-data-[orientation=vertical]/timeline:-translate-x-1/2 absolute size-4 rounded-full border-2 border-primary/20 group-data-[orientation=vertical]/timeline:top-0 group-data-[orientation=horizontal]/timeline:left-0 group-data-completed/timeline-item:border-primary", className),
      "data-slot": "timeline-indicator",
      children
    };
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(defaultProps, props);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t5 = {
      defaultTagName: t1,
      render: t2,
      props: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t4(t5);
}

// TimelineItem
interface TimelineItemProps extends useRender.ComponentProps<"div"> {
  step: number;
}
function TimelineItem(t0) {
  const $ = _c(3);
  const {
    step,
    className,
    render,
    children,
    ...props
  } = t0;
  const {
    activeStep
  } = useTimeline();
  const defaultProps = {
    className: cn("group/timeline-item relative flex flex-1 flex-col gap-0.5 group-data-[orientation=vertical]/timeline:ms-8 group-data-[orientation=horizontal]/timeline:mt-8 group-data-[orientation=horizontal]/timeline:not-last:pe-8 group-data-[orientation=vertical]/timeline:not-last:pb-6 has-[+[data-completed]]:**:data-[slot=timeline-separator]:bg-primary", className),
    "data-completed": step <= activeStep || undefined,
    "data-slot": "timeline-item",
    children
  };
  const t1 = "div";
  const t2 = mergeProps(defaultProps, props);
  let t3;
  if ($[0] !== render || $[1] !== t2) {
    t3 = {
      defaultTagName: t1,
      render,
      props: t2
    };
    $[0] = render;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return useRender(t3);
}

// TimelineSeparator
function TimelineSeparator(t0) {
  const $ = _c(9);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      children,
      ...props
    } = t0;
    const defaultProps = {
      "aria-hidden": true,
      className: cn("group-data-[orientation=horizontal]/timeline:-top-6 group-data-[orientation=horizontal]/timeline:-translate-y-1/2 group-data-[orientation=vertical]/timeline:-left-6 group-data-[orientation=vertical]/timeline:-translate-x-1/2 absolute self-start bg-primary/10 group-last/timeline-item:hidden group-data-[orientation=horizontal]/timeline:h-0.5 group-data-[orientation=vertical]/timeline:h-[calc(100%-1rem-0.25rem)] group-data-[orientation=horizontal]/timeline:w-[calc(100%-1rem-0.25rem)] group-data-[orientation=vertical]/timeline:w-0.5 group-data-[orientation=horizontal]/timeline:translate-x-4.5 group-data-[orientation=vertical]/timeline:translate-y-4.5", className),
      "data-slot": "timeline-separator",
      children
    };
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(defaultProps, props);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t5 = {
      defaultTagName: t1,
      render: t2,
      props: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t4(t5);
}

// TimelineTitle
function TimelineTitle(t0) {
  const $ = _c(9);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      children,
      ...props
    } = t0;
    const defaultProps = {
      className: cn("font-medium text-sm", className),
      "data-slot": "timeline-title",
      children
    };
    t4 = useRender;
    t1 = "h3";
    t2 = render;
    t3 = mergeProps(defaultProps, props);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t5 = {
      defaultTagName: t1,
      render: t2,
      props: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t4(t5);
}
export { Timeline, TimelineContent, TimelineDate, TimelineHeader, TimelineIndicator, TimelineItem, TimelineSeparator, TimelineTitle };